import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SignalCard from './components/SignalCard';
import SignalFilters from './components/SignalFilters';
import PerformanceMetrics from './components/PerformanceMetrics';
import AlertSettings from './components/AlertSettings';
import SignalDetails from './components/SignalDetails';

const TradingSignals = () => {
  const [user] = useState({
    name: "Alex Thompson",
    email: "alex.thompson@email.com",
    subscriptionTier: "pro"
  });

  const [filters, setFilters] = useState({
    signalType: 'all',
    confidence: 'all',
    timeframe: 'all',
    cryptocurrency: 'all',
    minConfidence: '',
    searchQuery: '',
    activeOnly: true,
    highUrgencyOnly: false,
    includeExpired: false
  });

  const [isAlertSettingsOpen, setIsAlertSettingsOpen] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [isSignalDetailsOpen, setIsSignalDetailsOpen] = useState(false);
  const [sortBy, setSortBy] = useState('confidence');
  const [sortOrder, setSortOrder] = useState('desc');

  // Mock signals data
  const mockSignals = [
    {
      id: 1,
      pair: "BTC/USD",
      exchange: "Binance",
      type: "buy",
      confidence: 87,
      entryPrice: 42150,
      targetPrice: 45200,
      stopLoss: 40800,
      urgency: "high",
      analysis: `Strong bullish momentum detected with RSI showing oversold recovery and MACD crossing above signal line. Volume confirmation suggests institutional accumulation. Breaking above key resistance at $42,000 with strong support at $40,800.`,
      generatedAt: "2 min ago",
      expiresAt: "4 hours",
      technicalData: {
        rsi: 65.4,
        macd: 0.0234,
        ma: 42150,
        volume: "1.2M",
        support: 40800,
        resistance: 43200
      },
      marketSentiment: 78,
      riskLevel: "Medium",
      riskRewardRatio: 2.8
    },
    {
      id: 2,
      pair: "ETH/USD",
      exchange: "Coinbase",
      type: "sell",
      confidence: 73,
      entryPrice: 2850,
      targetPrice: 2650,
      stopLoss: 2950,
      urgency: "medium",
      analysis: `Bearish divergence forming on 4H chart with declining volume. Price approaching overbought territory with potential rejection at $2,900 resistance. Consider taking profits or opening short positions.`,
      generatedAt: "5 min ago",
      expiresAt: "6 hours",
      technicalData: {
        rsi: 72.1,
        macd: -0.0156,
        ma: 2820,
        volume: "850K",
        support: 2750,
        resistance: 2900
      },
      marketSentiment: 45,
      riskLevel: "High",
      riskRewardRatio: 2.0
    },
    {
      id: 3,
      pair: "ADA/USD",
      exchange: "Kraken",
      type: "buy",
      confidence: 91,
      entryPrice: 0.485,
      targetPrice: 0.520,
      stopLoss: 0.465,
      urgency: "high",
      analysis: `Exceptional setup with golden cross formation and strong fundamentals. Network activity increasing significantly with upcoming protocol upgrade. Technical indicators align perfectly for upward momentum.`,
      generatedAt: "1 min ago",
      expiresAt: "3 hours",
      technicalData: {
        rsi: 58.3,
        macd: 0.0045,
        ma: 0.482,
        volume: "2.1M",
        support: 0.465,
        resistance: 0.495
      },
      marketSentiment: 85,
      riskLevel: "Low",
      riskRewardRatio: 3.5
    },
    {
      id: 4,
      pair: "SOL/USD",
      exchange: "FTX",
      type: "hold",
      confidence: 68,
      entryPrice: 95.50,
      targetPrice: 98.00,
      stopLoss: 92.00,
      urgency: "low",
      analysis: `Consolidation phase with mixed signals. Price trading within tight range with low volatility. Wait for clear breakout direction before taking position. Monitor for volume increase.`,
      generatedAt: "8 min ago",
      expiresAt: "12 hours",
      technicalData: {
        rsi: 52.7,
        macd: 0.0012,
        ma: 95.20,
        volume: "450K",
        support: 92.00,
        resistance: 98.50
      },
      marketSentiment: 55,
      riskLevel: "Medium",
      riskRewardRatio: 1.8
    },
    {
      id: 5,
      pair: "DOT/USD",
      exchange: "Binance",
      type: "buy",
      confidence: 82,
      entryPrice: 6.85,
      targetPrice: 7.40,
      stopLoss: 6.50,
      urgency: "medium",
      analysis: `Bullish flag pattern completion with strong volume confirmation. Parachain auctions driving positive sentiment. Technical breakout above $6.80 resistance with clear upside targets.`,
      generatedAt: "12 min ago",
      expiresAt: "8 hours",
      technicalData: {
        rsi: 61.9,
        macd: 0.0089,
        ma: 6.78,
        volume: "1.8M",
        support: 6.50,
        resistance: 7.10
      },
      marketSentiment: 72,
      riskLevel: "Medium",
      riskRewardRatio: 2.2
    },
    {
      id: 6,
      pair: "MATIC/USD",
      exchange: "Coinbase",
      type: "sell",
      confidence: 79,
      entryPrice: 0.92,
      targetPrice: 0.85,
      stopLoss: 0.96,
      urgency: "medium",
      analysis: `Double top formation confirmed with bearish momentum building. Network congestion concerns and competitive pressure from other L2 solutions. Technical indicators suggest downward pressure.`,
      generatedAt: "15 min ago",
      expiresAt: "10 hours",
      technicalData: {
        rsi: 68.4,
        macd: -0.0023,
        ma: 0.90,
        volume: "1.1M",
        support: 0.85,
        resistance: 0.94
      },
      marketSentiment: 38,
      riskLevel: "High",
      riskRewardRatio: 1.9
    }
  ];

  // Mock performance metrics
  const performanceMetrics = {
    accuracy: 84,
    accuracyChange: 3.2,
    averageReturn: 12.8,
    returnChange: -1.5,
    totalSignals: 1247,
    signalsChange: 8.7,
    winRate: 76,
    winRateChange: 2.1,
    buySignals: 523,
    buyAccuracy: 87,
    sellSignals: 412,
    sellAccuracy: 81,
    holdSignals: 312,
    holdAccuracy: 83
  };

  // Mock alert settings
  const [alertSettings, setAlertSettings] = useState({
    enableAlerts: true,
    highConfidenceOnly: false,
    urgentSignals: true,
    primaryMethod: 'email',
    secondaryMethod: 'push',
    email: 'alex.thompson@email.com',
    phone: '+1 (555) 123-4567',
    minConfidence: '70',
    watchlist: 'BTC, ETH, ADA',
    quietHours: true,
    quietStart: '22:00',
    quietEnd: '07:00',
    weekendAlerts: false
  });

  // Filter and sort signals
  const filteredSignals = mockSignals?.filter(signal => {
    if (filters?.signalType !== 'all' && signal?.type !== filters?.signalType) return false;
    if (filters?.confidence !== 'all') {
      const confidenceMap = {
        'high': 80,
        'medium': 60,
        'low': 0
      };
      const minConf = confidenceMap?.[filters?.confidence];
      const maxConf = filters?.confidence === 'low' ? 59 : 100;
      if (signal?.confidence < minConf || signal?.confidence > maxConf) return false;
    }
    if (filters?.cryptocurrency !== 'all' && !signal?.pair?.includes(filters?.cryptocurrency)) return false;
    if (filters?.minConfidence && signal?.confidence < parseInt(filters?.minConfidence)) return false;
    if (filters?.searchQuery && !signal?.pair?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase())) return false;
    if (filters?.highUrgencyOnly && signal?.urgency !== 'high') return false;
    
    return true;
  })?.sort((a, b) => {
    let aValue = a?.[sortBy];
    let bValue = b?.[sortBy];
    
    if (sortBy === 'confidence') {
      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    }
    
    return sortOrder === 'desc' ? bValue?.localeCompare(aValue) : aValue?.localeCompare(bValue);
  });

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      signalType: 'all',
      confidence: 'all',
      timeframe: 'all',
      cryptocurrency: 'all',
      minConfidence: '',
      searchQuery: '',
      activeOnly: true,
      highUrgencyOnly: false,
      includeExpired: false
    });
  };

  const handleViewDetails = (signal) => {
    setSelectedSignal(signal);
    setIsSignalDetailsOpen(true);
  };

  const handleExecuteTrade = (signal) => {
    // Mock trade execution
    alert(`Executing ${signal?.type?.toUpperCase()} order for ${signal?.pair} at $${signal?.entryPrice?.toLocaleString()}`);
  };

  const handleSaveAlertSettings = (newSettings) => {
    setAlertSettings(newSettings);
    alert('Alert settings saved successfully!');
  };

  const sortOptions = [
    { value: 'confidence', label: 'Confidence' },
    { value: 'urgency', label: 'Urgency' },
    { value: 'generatedAt', label: 'Time Generated' },
    { value: 'pair', label: 'Cryptocurrency' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        onLogin={() => window.location.href = '/user-login'}
        onLogout={() => window.location.href = '/user-login'}
        onUpgrade={() => window.location.href = '/subscription-management'}
      />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Trading Signals</h1>
              <p className="text-muted-foreground">
                AI-powered cryptocurrency trading recommendations with real-time analysis
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 md:mt-0">
              <Button
                variant="outline"
                onClick={() => setIsAlertSettingsOpen(true)}
                iconName="Settings"
                iconPosition="left"
              >
                Alert Settings
              </Button>
              <Button
                variant="default"
                onClick={() => window.location?.reload()}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh Signals
              </Button>
            </div>
          </div>

          {/* Performance Metrics */}
          <PerformanceMetrics metrics={performanceMetrics} />

          {/* Filters */}
          <SignalFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onResetFilters={handleResetFilters}
          />

          {/* Signals Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <h2 className="text-xl font-semibold text-foreground">
                Active Signals ({filteredSignals?.length})
              </h2>
              <div className="flex items-center space-x-2">
                <Icon name="Zap" size={16} className="text-success" />
                <span className="text-sm text-success font-medium">Live</span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-md text-sm bg-input text-foreground"
              >
                {sortOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    Sort by {option?.label}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                iconName={sortOrder === 'desc' ? 'ArrowDown' : 'ArrowUp'}
              />
            </div>
          </div>

          {/* Signals List */}
          <div className="space-y-4">
            {filteredSignals?.length > 0 ? (
              filteredSignals?.map(signal => (
                <SignalCard
                  key={signal?.id}
                  signal={signal}
                  onExecuteTrade={handleExecuteTrade}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-card-foreground mb-2">No signals found</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your filters or check back later for new signals.
                </p>
                <Button
                  variant="outline"
                  onClick={handleResetFilters}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Reset Filters
                </Button>
              </div>
            )}
          </div>

          {/* Load More */}
          {filteredSignals?.length > 0 && (
            <div className="text-center mt-8">
              <Button
                variant="outline"
                iconName="ChevronDown"
                iconPosition="right"
              >
                Load More Signals
              </Button>
            </div>
          )}
        </div>
      </main>
      {/* Modals */}
      <AlertSettings
        isOpen={isAlertSettingsOpen}
        onClose={() => setIsAlertSettingsOpen(false)}
        currentSettings={alertSettings}
        onSave={handleSaveAlertSettings}
      />
      <SignalDetails
        signal={selectedSignal}
        isOpen={isSignalDetailsOpen}
        onClose={() => setIsSignalDetailsOpen(false)}
        onExecuteTrade={handleExecuteTrade}
      />
    </div>
  );
};

export default TradingSignals;